import { Flex } from '@smarterservices/smarter-ui';
import SidebarPanel from './SidebarPanel';
import ExamPasswordsCard from './ExamPasswordsCard';
import ConnectionToolsCard from './ConnectionToolsCard';
import LockdownToolCard from './LockdownToolCard';

/**
 * RemoteControlIframe — Renders the Upscope co-browsing iframe and sidebar.
 *
 * The iframe is always mounted (hidden via CSS visibility) once a watchUrl is available
 * to maintain the postMessage communication channel with Upscope. The sidebar with
 * exam passwords and connection tools is only shown when the session is active.
 *
 * @param {Object} props
 * @param {string} props.watchUrl - The Upscope co-browsing watch URL
 * @param {boolean} props.isVisible - Whether the iframe and sidebar should be visible
 * @param {Function} props.onIframeLoad - Callback fired when the iframe finishes loading
 * @param {string} props.uniquePassword - The unique exam password (safe to share)
 * @param {string} props.actualPassword - The actual exam password (confidential)
 * @param {Function} props.onReconnect - Callback to re-inject the remote control script
 * @param {boolean} props.isReconnecting - Whether a reconnect attempt is in progress
 * @param {boolean} props.showLockdownTool - Whether to show the lockdown & start tool
 * @param {Function} props.onLockdownAndStart - Callback to invoke ExamLockdownAndStart
 * @returns {JSX.Element|null}
 */
function RemoteControlIframe({
  watchUrl,
  isVisible,
  onIframeLoad,
  uniquePassword,
  actualPassword,
  onReconnect,
  isReconnecting,
  showLockdownTool,
  onLockdownAndStart,
}) {
  if (!watchUrl) return null;

  return (
    <Flex height="100%" width="100%" style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
      <Flex.Item shouldGrow shouldShrink height="100%">
        <iframe
          id="upscope-iframe"
          src={watchUrl}
          onLoad={onIframeLoad}
          title="Remote Control Session"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          allow="clipboard-read; clipboard-write; screen-wake-lock"
        />
      </Flex.Item>
      {isVisible && (
        <SidebarPanel>
          <ExamPasswordsCard
            uniquePassword={uniquePassword}
            actualPassword={actualPassword}
          />
          {showLockdownTool && (
            <LockdownToolCard onLockdownAndStart={onLockdownAndStart} />
          )}
          <ConnectionToolsCard
            onReconnect={onReconnect}
            isReconnecting={isReconnecting}
          />
        </SidebarPanel>
      )}
    </Flex>
  );
}

export default RemoteControlIframe;
